import { useCallback, useMemo, useRef, useState, type ComponentProps, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Switch from 'LuxKit/Switch/Switch'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import LineArrowUpIcon from 'LuxKit/Icons/line/LineArrowUpIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type TripListView from './TripListView'
import StopTimingEditControl from '../StopTimingEditControl'
import LocationFilterMenuItem from './LocationFilterMenuItem'
import StopSearchActions from './StopSearchActions'
import StatusFilterMenuItem from './StatusFilterMenuItem'
import TypeFilterMenuItem from './TypeFilterMenuItem'
import {
  canMoveTripListRowToAnotherDay,
  getTripListBulkMoveDayOptions,
} from './tripListMoveDayOptions'
import type { ItineraryScrollTarget } from '../../hooks/useItineraryUpdates'
import { useItineraryScrollTarget } from '../../hooks/useItineraryScrollTarget'
import TripListMobileRowCard from './TripListMobileRowCard'
import TripListQuickActions from './TripListQuickActions'
import { getTripListStatusLabel } from './tripListStatus'
import { getTripListTypeLabel } from './tripListTypeLabels'
import {
  getTripListDateFilterOptions,
  getTripListLocationFilterOptions,
  getTripListRowCountByStatus,
  getTripListRowCountByType,
  getTripListStatusFilterOptions,
  getTripListTypeFilterOptions,
  hasTripListDateFilterOptions,
  hasTripListLocationFilterOptions,
  hasTripListStatusFilterOptions,
  hasTripListTypeFilterOptions,
} from './tripListFilterOptions'
import { useCloseDropdownWhenUnavailable } from './useCloseDropdownWhenUnavailable'
import type { TripListRowType, TripListStatusValue } from './tripListTypes'

type TripListViewProps = ComponentProps<typeof TripListView>
type TripListMobileGroup = TripListViewProps['groups'][number]

const TRIP_LIST_MOBILE_SCROLL_SELECTORS = {
  item: (target: ItineraryScrollTarget) => target.itemId !== undefined ? `[data-item-id="${target.itemId}"]` : undefined,
  day: (target: ItineraryScrollTarget) => target.dayId !== undefined ? `[data-day-id="${target.dayId}"]` : undefined,
  stop: (target: ItineraryScrollTarget) => `[data-stop-id="${target.stopId}"]`,
}

const MobileListPanel = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const ScrollArea = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: ${rem(24)};

  &.has-actions {
    padding-bottom: ${rem(112)};
  }
`

const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  padding: ${rem(16)} 0 ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const FilterScroller = styled.div`
  display: flex;
  gap: ${rem(10)};
  align-items: center;
  min-width: 0;
  overflow-x: auto;
  padding: 0 ${rem(16)} ${rem(2)};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const SelectionToolbar = styled.div`
  padding: ${rem(16)} ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CancelSelectionButton = styled.button`
  display: inline-flex;
  height: ${rem(38)};
  align-items: center;
  justify-content: center;
  padding: 0 ${rem(18)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;
`

const FilterChipButton = styled.button`
  display: inline-flex;
  height: ${rem(38)};
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: ${rem(7)};
  padding: 0 ${rem(14)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;

  &.is-active {
    border-color: ${props => props.theme.palette.brand.primary.normal};
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &:disabled,
  &:disabled:hover {
    cursor: default;
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    svg {
      color: currentColor;
    }
  }
`

const FilterChipText = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  font-weight: 400;
  white-space: nowrap;
`

const FilterChipDivider = styled.div`
  width: ${rem(1)};
  height: ${rem(32)};
  flex: 0 0 auto;
  background: ${props => props.theme.palette.neutral.surface.lightForeground};
`

const FilterMenuPanel = styled.div`
  display: flex;
  width: ${rem(264)};
  max-height: ${rem(360)};
  flex-direction: column;
`

const FilterMenuHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: ${rem(4)} ${rem(16)} ${rem(2)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const FilterMenuContent = styled.div`
  overflow-y: auto;
  padding: 0 ${rem(16)};
`

const FilterMenuFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  padding: ${rem(16)} ${rem(16)} 0;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const ClearFilterButton = styled(TextButton)`
  min-width: 0;
  padding-inline: 0;
`

const LuxPlusToggle = styled.label`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${rem(8)};
  height: ${rem(32)};
  padding: 0 ${rem(10)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &:has(:disabled) {
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const GroupStack = styled.div`
  display: flex;
  flex-direction: column;
`

const DayGroup = styled.section`
  display: flex;
  flex-direction: column;
`

const StopHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${rem(8)};
  padding: ${rem(12)} ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  border-start-start-radius: ${props => props.theme.borderRadius.L};
  border-start-end-radius: ${props => props.theme.borderRadius.L};
`

const StopHeaderTitle = styled(BodyText)`
  min-width: 0;
`

const StopHeaderSubtitle = styled(Caption)`
  min-width: 0;
  max-width: 100%;
  color: inherit;
  opacity: 0.82;
`

interface MobileStopHeaderProps {
  group: TripListMobileGroup
  addDaysOrDatesLabel: string
  actionsDisabled: boolean
  isSelectionMode: boolean
  onSendStopTimingEditMessage: TripListViewProps['onSendStopTimingEditMessage']
}

function MobileStopHeader({
  group,
  addDaysOrDatesLabel,
  actionsDisabled,
  isSelectionMode,
  onSendStopTimingEditMessage,
}: MobileStopHeaderProps) {
  const stopHeaderRef = useRef<HTMLDivElement>(null)
  const stopHeader = group.stopHeader

  if (!stopHeader) return null

  return (
    <StopHeader ref={stopHeaderRef} data-stop-id={stopHeader.stop.id}>
      <StopHeaderTitle variant="medium" weight="bold" lineClamp={1}>
        {stopHeader.title}
      </StopHeaderTitle>
      <StopHeaderSubtitle variant="large">
        <StopTimingEditControl
          stop={stopHeader.stop}
          onSendMessage={onSendStopTimingEditMessage}
          disabled={actionsDisabled || isSelectionMode}
          variant="mobile"
          dropdownAnchorRef={stopHeaderRef}
          expandTapTarget
        >
          {stopHeader.subtitle || addDaysOrDatesLabel}
        </StopTimingEditControl>
      </StopHeaderSubtitle>
    </StopHeader>
  )
}

const DayHeading = styled.div`
  padding: ${rem(12)} ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const EmptyStopPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(12)};
  padding: ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const EmptyStopBrowseActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const EmptyState = styled.div`
  display: flex;
  min-height: ${rem(220)};
  align-items: center;
  justify-content: center;
  padding: ${rem(24)};
  text-align: center;
`

function getSortIcon(sort: TripListViewProps['sort']) {
  if (sort?.field !== 'price') return <LineAngleDownIcon size="XS" />
  return sort.direction === 'asc' ? <LineArrowUpIcon size="XS" /> : <LineArrowDownIcon size="XS" />
}

function TripListMobileView({
  groups,
  tripStops,
  allRows,
  visibleRows,
  selectedRows,
  selectedRowIds,
  filters,
  sort,
  luxPlusEnabled,
  onSelectRow,
  onDateFiltersChange,
  onLocationFiltersChange,
  onTypeFiltersChange,
  onStatusFiltersChange,
  onClearFilters,
  onSortToggle,
  onLuxPlusToggle,
  onRowClick,
  onBookRow,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onAskAiSelectedRows,
  onAskAiMoveSelectedRows,
  onDelete,
  onMoveToDay,
  onMoveSelectedRowsToDay,
  onDeleteSelectedRows,
  onClearSelection,
  onOpenStopHotels,
  onOpenStopFlights,
  onOpenStopExperiences,
  onSendStopTimingEditMessage,
  onUpdateNote,
  onUpdateStatus,
  bookingRowId,
  noteUpdateRowId,
  statusUpdateRowId,
  notesDisabled = false,
  statusDisabled = false,
  actionsDisabled = false,
  scrollTarget,
  onScrollComplete,
}: TripListViewProps) {
  const intl = useIntl()
  const [dateFilterOpen, setDateFilterOpen] = useState(false)
  const [locationFilterOpen, setLocationFilterOpen] = useState(false)
  const [typeFilterOpen, setTypeFilterOpen] = useState(false)
  const [statusFilterOpen, setStatusFilterOpen] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false)
  const dateFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const locationFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const typeFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const statusFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  useItineraryScrollTarget({
    scrollTarget,
    containerRef: scrollAreaRef,
    selectors: TRIP_LIST_MOBILE_SCROLL_SELECTORS,
    onScrollComplete,
  })

  const bookableRows = useMemo(() => visibleRows.filter(row => row.isBookable && !row.isBooked), [visibleRows])
  const hasVisibleRows = visibleRows.length > 0
  const selectedBookRow = selectedRows.length === 1 ? selectedRows[0] : undefined
  const showSelectedBookAction = Boolean(selectedBookRow && !selectedBookRow.isBooked)
  const selectedRowsCanMoveToDay = selectedRows.length > 0 && selectedRows.every(canMoveTripListRowToAnotherDay)
  const selectedMoveDayOptions = useMemo(
    () => selectedRowsCanMoveToDay ? getTripListBulkMoveDayOptions(selectedRows, tripStops, intl) : [],
    [intl, selectedRows, selectedRowsCanMoveToDay, tripStops],
  )
  const showSelectedMoveWithAi = selectedRows.length > 0 && !selectedRowsCanMoveToDay
  const activeDateFilterCount = filters.dates.size
  const activeLocationFilterCount = filters.locations.size
  const activeTypeFilterCount = filters.types.size + (filters.bookableOnly ? 1 : 0)
  const activeStatusFilterCount = filters.statuses.size
  const hasActiveSort = Boolean(sort)
  const hasActiveFilters = activeDateFilterCount > 0 || activeLocationFilterCount > 0 || activeTypeFilterCount > 0 || activeStatusFilterCount > 0 || hasActiveSort
  const isSelectionMode = selectionMode || selectedRows.length > 0

  const dateFilterOptions = useMemo(() => getTripListDateFilterOptions(allRows), [allRows])
  const hasDateFilterOptions = hasTripListDateFilterOptions(allRows)
  const locationFilterOptions = useMemo(() => getTripListLocationFilterOptions(allRows), [allRows])
  const hasLocationFilterOptions = hasTripListLocationFilterOptions(allRows)
  const rowCountByType = useMemo(() => getTripListRowCountByType(allRows), [allRows])
  const typeFilterOptions = useMemo(() => getTripListTypeFilterOptions(rowCountByType), [rowCountByType])
  const hasTypeFilterOptions = hasTripListTypeFilterOptions(typeFilterOptions, bookableRows.length)
  const rowCountByStatus = useMemo(() => getTripListRowCountByStatus(allRows), [allRows])
  const statusFilterOptions = useMemo(() => getTripListStatusFilterOptions(rowCountByStatus), [rowCountByStatus])
  const hasStatusFilterOptions = hasTripListStatusFilterOptions(allRows)
  const typeLabelByType = useMemo<Record<TripListRowType, string>>(() => ({
    accommodation: getTripListTypeLabel(intl, 'accommodation'),
    experience: getTripListTypeLabel(intl, 'experience'),
    dining: getTripListTypeLabel(intl, 'dining'),
    flight: getTripListTypeLabel(intl, 'flight'),
    transport: getTripListTypeLabel(intl, 'transport'),
    cruise: getTripListTypeLabel(intl, 'cruise'),
    activity: getTripListTypeLabel(intl, 'activity'),
  }), [intl])

  const locationFilterLabel = intl.formatMessage({
    id: 'wL7VwjdiJ0_VYSYdHG-i',
    defaultMessage: 'Location',
    description: 'Filter chip label for locations in the mobile TripChat itinerary list view',
  })
  const dateFilterLabel = intl.formatMessage({
    id: 'aas_-qQnld7hAbKqjrO4',
    defaultMessage: 'Date',
    description: 'Date filter chip label in the trip planner list view',
  })
  const typeFilterLabel = intl.formatMessage({
    id: 'xo44I0qETkFUA6h4hsT_',
    defaultMessage: 'Type',
    description: 'Filter chip label for row types in the mobile TripChat itinerary list view',
  })
  const statusFilterLabel = intl.formatMessage({
    id: 'J2PAmmwxPPcYE8d-TnX1',
    defaultMessage: 'Status',
    description: 'Filter chip label for status values in the mobile TripChat itinerary list view',
  })
  const priceSortLabel = intl.formatMessage({
    id: '3fonh5ADDhpHAnfeOqxV',
    defaultMessage: 'Price',
    description: 'Sort chip label for prices in the mobile TripChat itinerary list view',
  })
  const clearFilterLabel = intl.formatMessage({
    id: 'lx-1DfGB6HXyd3sQ22Lv',
    defaultMessage: 'Clear filters',
    description: 'Button label for clearing filters in the mobile TripChat itinerary list view',
  })
  const filterMenuHeading = intl.formatMessage({
    id: 'K7NJCG5V2k_HlywrmC1f',
    defaultMessage: 'Filter',
    description: 'Dropdown heading for filter options in the mobile TripChat itinerary list view',
  })
  const clearLabel = intl.formatMessage({
    id: '5LctbpMAf3CPkFHNlvJb',
    defaultMessage: 'Clear',
    description: 'Button label for clearing one filter group in the mobile TripChat itinerary list view',
  })
  const bookableOnlyLabel = intl.formatMessage({
    id: 'j0Xmt8DtsmL71R-Bxooj',
    defaultMessage: 'Bookable only',
    description: 'Filter option label for showing only bookable mobile TripChat itinerary list rows',
  })
  const emptyStateLabel = intl.formatMessage({
    id: '_UyUJ_zU_m1kBjb_NaP1',
    defaultMessage: 'No itinerary items match these filters.',
    description: 'Empty state shown when mobile TripChat list filters remove all visible rows',
  })
  const selectVisibleLabel = intl.formatMessage({
    id: '2ml0Vs24Caua3wrTeIrT',
    defaultMessage: 'Select',
    description: 'Compact chip label for selecting all visible rows in the mobile TripChat itinerary list view',
  })
  const cancelSelectionLabel = intl.formatMessage({
    id: 'aig_YCtPqbzdOU6KEhVS',
    defaultMessage: 'Cancel',
    description: 'Button label for exiting selection mode in the mobile TripChat itinerary list view',
  })
  const sortByPriceLabel = intl.formatMessage({
    id: 'ncrSZD71Ff065xEJElWi',
    defaultMessage: 'Sort by price',
    description: 'Accessible label for sorting mobile TripChat itinerary list rows by price',
  })
  const addDaysOrDatesLabel = intl.formatMessage({
    id: 'tCy9PycI1Guoepw6mk8q',
    defaultMessage: 'Add days or dates',
    description: 'Action text shown on a trip planner stop with no dates or day count',
  })

  const handleDateFilterToggle = useCallback((date: string) => {
    const next = new Set(filters.dates)
    if (next.has(date)) {
      next.delete(date)
    } else {
      next.add(date)
    }
    onDateFiltersChange(next)
  }, [filters.dates, onDateFiltersChange])

  const handleLocationFilterToggle = useCallback((location: string) => {
    const next = new Set(filters.locations)
    if (next.has(location)) {
      next.delete(location)
    } else {
      next.add(location)
    }
    onLocationFiltersChange(next)
  }, [filters.locations, onLocationFiltersChange])

  const handleTypeFilterToggle = useCallback((type: TripListRowType) => {
    const next = new Set(filters.types)
    if (next.has(type)) {
      next.delete(type)
    } else {
      next.add(type)
    }
    onTypeFiltersChange(next, filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleBookableOnlyToggle = useCallback(() => {
    onTypeFiltersChange(new Set(filters.types), !filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleStatusFilterToggle = useCallback((status: TripListStatusValue) => {
    const next = new Set(filters.statuses)
    if (next.has(status)) {
      next.delete(status)
    } else {
      next.add(status)
    }
    onStatusFiltersChange(next)
  }, [filters.statuses, onStatusFiltersChange])

  const toggleDateFilter = useCallback(() => {
    if (!hasDateFilterOptions) return
    setDateFilterOpen(prev => !prev)
  }, [hasDateFilterOptions])

  const closeDateFilter = useCallback(() => {
    setDateFilterOpen(false)
  }, [])

  const toggleLocationFilter = useCallback(() => {
    if (!hasLocationFilterOptions) return
    setLocationFilterOpen(prev => !prev)
  }, [hasLocationFilterOptions])

  const closeLocationFilter = useCallback(() => {
    setLocationFilterOpen(false)
  }, [])

  const toggleTypeFilter = useCallback(() => {
    if (!hasTypeFilterOptions) return
    setTypeFilterOpen(prev => !prev)
  }, [hasTypeFilterOptions])

  const closeTypeFilter = useCallback(() => {
    setTypeFilterOpen(false)
  }, [])

  const toggleStatusFilter = useCallback(() => {
    if (!hasStatusFilterOptions) return
    setStatusFilterOpen(prev => !prev)
  }, [hasStatusFilterOptions])

  const closeStatusFilter = useCallback(() => {
    setStatusFilterOpen(false)
  }, [])

  const handleClearDateFilters = useCallback(() => {
    onDateFiltersChange(new Set())
  }, [onDateFiltersChange])

  const handleClearLocationFilters = useCallback(() => {
    onLocationFiltersChange(new Set())
  }, [onLocationFiltersChange])

  const handleClearTypeFilters = useCallback(() => {
    onTypeFiltersChange(new Set(), false)
  }, [onTypeFiltersChange])

  const handleClearStatusFilters = useCallback(() => {
    onStatusFiltersChange(new Set())
  }, [onStatusFiltersChange])

  const handleClearAllFilters = useCallback(() => {
    setDateFilterOpen(false)
    setLocationFilterOpen(false)
    setTypeFilterOpen(false)
    setStatusFilterOpen(false)
    onClearFilters()
  }, [onClearFilters])

  const handlePriceSortClick = useCallback(() => {
    onSortToggle('price')
  }, [onSortToggle])

  const handleSelectionModeOpen = useCallback(() => {
    setDateFilterOpen(false)
    setLocationFilterOpen(false)
    setTypeFilterOpen(false)
    setStatusFilterOpen(false)
    setSelectionMode(true)
  }, [])

  const handleSelectionModeCancel = useCallback(() => {
    setSelectionMode(false)
    onClearSelection()
  }, [onClearSelection])

  const handleSelectedBook = useCallback(() => {
    if (selectedBookRow && !selectedBookRow.isBooked) {
      onBookRow(selectedBookRow)
    }
  }, [onBookRow, selectedBookRow])

  const stopControlEvent = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  useCloseDropdownWhenUnavailable(hasDateFilterOptions, setDateFilterOpen)
  useCloseDropdownWhenUnavailable(hasLocationFilterOptions, setLocationFilterOpen)
  useCloseDropdownWhenUnavailable(hasTypeFilterOptions, setTypeFilterOpen)
  useCloseDropdownWhenUnavailable(hasStatusFilterOptions, setStatusFilterOpen)

  return (
    <MobileListPanel data-testid="trip-list-mobile-view">
      <ScrollArea ref={scrollAreaRef} className={clsx(selectedRows.length > 0 && 'has-actions')}>
        {!isSelectionMode && (
          <Toolbar>
            <FilterScroller onClick={stopControlEvent}>
              <FilterChipButton
                type="button"
                onClick={handleSelectionModeOpen}
                disabled={actionsDisabled || !hasVisibleRows}
              >
                <FilterChipText>{selectVisibleLabel}</FilterChipText>
              </FilterChipButton>
              <FilterChipDivider aria-hidden="true" />
              <FilterChipButton
                ref={dateFilterTriggerRef}
                type="button"
                onClick={toggleDateFilter}
                className={activeDateFilterCount > 0 ? 'is-active' : undefined}
                disabled={!hasDateFilterOptions}
              >
                <FilterChipText>{dateFilterLabel}</FilterChipText>
                {activeDateFilterCount > 0 && <Caption variant="medium" weight="bold">{activeDateFilterCount}</Caption>}
                <LineAngleDownIcon size="XS" />
              </FilterChipButton>
              {hasDateFilterOptions && (
                <DropdownList
                  size="S"
                  open={dateFilterOpen}
                  onClose={closeDateFilter}
                  triggerRef={dateFilterTriggerRef}
                  anchorRef={dateFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <FilterMenuPanel>
                    <FilterMenuHeader>
                      <BodyText variant="large" weight="bold">{filterMenuHeading}</BodyText>
                    </FilterMenuHeader>
                    <FilterMenuContent>
                      {dateFilterOptions.map(({ value, label, count }) => (
                        <LocationFilterMenuItem
                          key={value}
                          value={value}
                          label={label}
                          count={count}
                          selected={filters.dates.has(value)}
                          onToggle={handleDateFilterToggle}
                        />
                      ))}
                    </FilterMenuContent>
                    <FilterMenuFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearDateFilters}>
                        {clearLabel}
                      </ClearFilterButton>
                    </FilterMenuFooter>
                  </FilterMenuPanel>
                </DropdownList>
              )}
              <FilterChipButton
                ref={locationFilterTriggerRef}
                type="button"
                onClick={toggleLocationFilter}
                className={activeLocationFilterCount > 0 ? 'is-active' : undefined}
                disabled={!hasLocationFilterOptions}
              >
                <FilterChipText>{locationFilterLabel}</FilterChipText>
                {activeLocationFilterCount > 0 && <Caption variant="medium" weight="bold">{activeLocationFilterCount}</Caption>}
                <LineAngleDownIcon size="XS" />
              </FilterChipButton>
              {hasLocationFilterOptions && (
                <DropdownList
                  size="S"
                  open={locationFilterOpen}
                  onClose={closeLocationFilter}
                  triggerRef={locationFilterTriggerRef}
                  anchorRef={locationFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <FilterMenuPanel>
                    <FilterMenuHeader>
                      <BodyText variant="large" weight="bold">{filterMenuHeading}</BodyText>
                    </FilterMenuHeader>
                    <FilterMenuContent>
                      {locationFilterOptions.map(({ location, count }) => (
                        <LocationFilterMenuItem
                          key={location.filterValue}
                          value={location.filterValue}
                          label={location.label}
                          count={count}
                          selected={filters.locations.has(location.filterValue)}
                          onToggle={handleLocationFilterToggle}
                        />
                      ))}
                    </FilterMenuContent>
                    <FilterMenuFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearLocationFilters}>
                        {clearLabel}
                      </ClearFilterButton>
                    </FilterMenuFooter>
                  </FilterMenuPanel>
                </DropdownList>
              )}
              <FilterChipButton
                ref={typeFilterTriggerRef}
                type="button"
                onClick={toggleTypeFilter}
                className={activeTypeFilterCount > 0 ? 'is-active' : undefined}
                disabled={!hasTypeFilterOptions}
              >
                <FilterChipText>{typeFilterLabel}</FilterChipText>
                {activeTypeFilterCount > 0 && <Caption variant="medium" weight="bold">{activeTypeFilterCount}</Caption>}
                <LineAngleDownIcon size="XS" />
              </FilterChipButton>
              {hasTypeFilterOptions && (
                <DropdownList
                  size="S"
                  open={typeFilterOpen}
                  onClose={closeTypeFilter}
                  triggerRef={typeFilterTriggerRef}
                  anchorRef={typeFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <FilterMenuPanel>
                    <FilterMenuHeader>
                      <BodyText variant="large" weight="bold">{filterMenuHeading}</BodyText>
                    </FilterMenuHeader>
                    <FilterMenuContent>
                      {typeFilterOptions.map(type => (
                        <TypeFilterMenuItem
                          key={type}
                          type={type}
                          label={typeLabelByType[type]}
                          count={rowCountByType[type]}
                          selected={filters.types.has(type)}
                          onToggle={handleTypeFilterToggle}
                        />
                      ))}
                      <LocationFilterMenuItem
                        value="bookableOnly"
                        label={bookableOnlyLabel}
                        count={bookableRows.length}
                        selected={filters.bookableOnly}
                        onToggle={handleBookableOnlyToggle}
                      />
                    </FilterMenuContent>
                    <FilterMenuFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearTypeFilters}>
                        {clearLabel}
                      </ClearFilterButton>
                    </FilterMenuFooter>
                  </FilterMenuPanel>
                </DropdownList>
              )}
              <FilterChipButton
                ref={statusFilterTriggerRef}
                type="button"
                onClick={toggleStatusFilter}
                className={activeStatusFilterCount > 0 ? 'is-active' : undefined}
                disabled={!hasStatusFilterOptions}
              >
                <FilterChipText>{statusFilterLabel}</FilterChipText>
                {activeStatusFilterCount > 0 && <Caption variant="medium" weight="bold">{activeStatusFilterCount}</Caption>}
                <LineAngleDownIcon size="XS" />
              </FilterChipButton>
              {hasStatusFilterOptions && (
                <DropdownList
                  size="S"
                  open={statusFilterOpen}
                  onClose={closeStatusFilter}
                  triggerRef={statusFilterTriggerRef}
                  anchorRef={statusFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <FilterMenuPanel>
                    <FilterMenuHeader>
                      <BodyText variant="large" weight="bold">{filterMenuHeading}</BodyText>
                    </FilterMenuHeader>
                    <FilterMenuContent>
                      {statusFilterOptions.map(status => (
                        <StatusFilterMenuItem
                          key={status}
                          status={status}
                          label={getTripListStatusLabel(intl, status)}
                          count={rowCountByStatus[status]}
                          selected={filters.statuses.has(status)}
                          onToggle={handleStatusFilterToggle}
                        />
                      ))}
                    </FilterMenuContent>
                    <FilterMenuFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearStatusFilters}>
                        {clearLabel}
                      </ClearFilterButton>
                    </FilterMenuFooter>
                  </FilterMenuPanel>
                </DropdownList>
              )}
              <FilterChipButton
                type="button"
                onClick={handlePriceSortClick}
                className={sort?.field === 'price' ? 'is-active' : undefined}
                aria-label={sortByPriceLabel}
                disabled={!hasVisibleRows}
              >
                <FilterChipText>{priceSortLabel}</FilterChipText>
                {getSortIcon(sort)}
              </FilterChipButton>
              {hasActiveFilters && (
                <FilterChipButton type="button" onClick={handleClearAllFilters}>
                  <LineTimesIcon size="XS" />
                  <FilterChipText>{clearFilterLabel}</FilterChipText>
                </FilterChipButton>
              )}
              <LuxPlusToggle>
                <LuxPlusLogoWrapper height={10} />
                <ProductPaletteProvider palette="lux-plus">
                  <Switch checked={luxPlusEnabled} onChange={onLuxPlusToggle} disabled={!hasVisibleRows} />
                </ProductPaletteProvider>
              </LuxPlusToggle>
            </FilterScroller>
          </Toolbar>
        )}
        {isSelectionMode && (
          <SelectionToolbar>
            <CancelSelectionButton type="button" onClick={handleSelectionModeCancel}>
              <FilterChipText>{cancelSelectionLabel}</FilterChipText>
            </CancelSelectionButton>
          </SelectionToolbar>
        )}
        {groups.length === 0 && (
          <EmptyState>
            <BodyText variant="medium" colour="light-foreground-alt">
              {emptyStateLabel}
            </BodyText>
          </EmptyState>
        )}
        {groups.length > 0 && (
          <GroupStack>
            {groups.map(group => (
              <DayGroup key={group.id}>
                <MobileStopHeader
                  group={group}
                  addDaysOrDatesLabel={addDaysOrDatesLabel}
                  actionsDisabled={actionsDisabled}
                  isSelectionMode={isSelectionMode}
                  onSendStopTimingEditMessage={onSendStopTimingEditMessage}
                />
                <DayHeading>
                  <BodyText variant="medium" weight="bold">{group.title}</BodyText>
                  {group.subtitle && (
                    <Caption variant="large" colour="light-foreground-alt">
                      {group.subtitle}
                    </Caption>
                  )}
                </DayHeading>
                {group.rows.map(row => (
                  <TripListMobileRowCard
                    key={row.id}
                    row={row}
                    tripStops={tripStops}
                    selected={selectedRowIds.has(row.id)}
                    selectionMode={isSelectionMode}
                    luxPlusEnabled={luxPlusEnabled}
                    isBookingPending={bookingRowId === row.id}
                    isNoteUpdatePending={noteUpdateRowId === row.id}
                    isStatusUpdatePending={statusUpdateRowId === row.id}
                    notesDisabled={notesDisabled}
                    statusDisabled={statusDisabled}
                    actionsDisabled={actionsDisabled}
                    onSelectRow={onSelectRow}
                    onRowClick={onRowClick}
                    onBookRow={onBookRow}
                    onViewBooking={onViewBooking}
                    onAskAi={onAskAi}
                    onAskAiMove={onAskAiMove}
                    onDelete={onDelete}
                    onMoveToDay={onMoveToDay}
                    onUpdateNote={onUpdateNote}
                    onUpdateStatus={onUpdateStatus}
                  />
                ))}
                {group.showEmptyPlaceholder && (
                  <EmptyStopPlaceholder>
                    <BodyText variant="medium" colour="light-foreground-alt">
                      {intl.formatMessage({
                        id: 'dukav_e-eCPWiO_9pp-2',
                        defaultMessage: "You don't have anything planned in {placeName} yet",
                        description: 'Placeholder text for an empty stop in the trip planner list view',
                      }, {
                        placeName: group.stopHeader?.title ?? group.title,
                      })}
                    </BodyText>
                    <EmptyStopBrowseActions>
                      {group.stopHeader && group.stopSearchActions && (
                        <StopSearchActions
                          actions={group.stopSearchActions}
                          stop={group.stopHeader.stop}
                          variant="default"
                          onOpenStopHotels={onOpenStopHotels}
                          onOpenStopFlights={onOpenStopFlights}
                          onOpenStopExperiences={onOpenStopExperiences}
                        />
                      )}
                    </EmptyStopBrowseActions>
                  </EmptyStopPlaceholder>
                )}
              </DayGroup>
            ))}
          </GroupStack>
        )}
      </ScrollArea>
      {selectedRows.length > 0 && (
        <TripListQuickActions
          variant="mobile"
          selectedCount={selectedRows.length}
          disabled={actionsDisabled}
          showBookAction={showSelectedBookAction}
          moveDayOptions={selectedMoveDayOptions}
          showMoveWithAi={showSelectedMoveWithAi}
          onBook={handleSelectedBook}
          onAskAi={onAskAiSelectedRows}
          onMoveToDay={onMoveSelectedRowsToDay}
          onMoveWithAi={onAskAiMoveSelectedRows}
          onDelete={onDeleteSelectedRows}
        />
      )}
    </MobileListPanel>
  )
}

export default TripListMobileView
